import { oc } from "@orpc/contract";
import { z } from "zod";
import { CatalogAdvancedFilterSchema } from "./products.contract";

const UUIDSchema = z.uuid();

// ─── Item lists ───────────────────────────────────────────────────────────────
// Org-shared, durable sets of catalog products. Any product-writer can edit or
// delete any list; duplicate names are allowed. A list can scope the catalog
// (products.listCatalog/countCatalog `itemListId`) and seed a new review
// (metadataChangeSets.stageFromItemList).

export const ItemListSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  // Number of products in the list.
  itemCount: z.number().int().nonnegative(),
  createdByUserId: UUIDSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ListItemListsResponseSchema = z.object({
  lists: z.array(ItemListSchema),
});

export const GetItemListInputSchema = z.object({ id: UUIDSchema });

export const GetItemListResponseSchema = z.object({
  list: ItemListSchema.nullable(),
});

// The two explicit-input sources: catalog product keys (from a selection) or
// raw item numbers (pasted AS/400 ITEMNOs, resolved server-side by display
// SKU). Both drop-and-report entries that match nothing (`unmatchedInputs`).
const ProductKeysSourceSchema = z.object({
  type: z.literal("productKeys"),
  productKeys: z.array(z.string().min(1)).min(1).max(50000),
});

const ItemNumbersSourceSchema = z.object({
  type: z.literal("itemNumbers"),
  itemNumbers: z.array(z.string().trim().min(1)).min(1).max(50000),
});

// Every product matching a catalog search + filters, resolved server-side in
// batches — the same shape as metadataChangeSets.stageAll.
const CatalogFiltersSourceSchema = z.object({
  type: z.literal("catalogFilters"),
  search: z.string().trim().optional(),
  searchMode: z.enum(["text", "regex"]).default("text"),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
});

/** Where a new list's products come from. */
export const ItemListSourceSchema = z.discriminatedUnion("type", [
  ProductKeysSourceSchema,
  ItemNumbersSourceSchema,
  CatalogFiltersSourceSchema,
]);

/** Sources accepted by addItems — explicit inputs only, no filter resolution. */
export const ItemListItemsSourceSchema = z.discriminatedUnion("type", [
  ProductKeysSourceSchema,
  ItemNumbersSourceSchema,
]);

// Same validity gate as products.contract's refineCatalogSearch, applied to
// the nested source so the issue lands on ["source", "search"].
function refineSourceCatalogSearch(
  val: { source: z.infer<typeof ItemListSourceSchema> },
  ctx: z.RefinementCtx,
): void {
  if (val.source.type !== "catalogFilters") return;
  if (val.source.searchMode === "regex" && val.source.search) {
    try {
      new RegExp(val.source.search);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["source", "search"],
        message: "Invalid regular expression.",
      });
    }
  }
}

export const CreateItemListInputSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    description: z.string().trim().max(2000).optional(),
    source: ItemListSourceSchema,
  })
  .superRefine(refineSourceCatalogSearch);

export const CreateItemListResponseSchema = z.object({
  listId: UUIDSchema,
  // Number of products in the new list.
  itemCount: z.number().int().nonnegative(),
  // Inputs (product keys or raw item numbers) that matched nothing — reported
  // rather than failing the create, so no loss is silent.
  unmatchedInputs: z.array(z.string()),
});

export const AddItemListItemsInputSchema = z.object({
  id: UUIDSchema,
  source: ItemListItemsSourceSchema,
});

export const AddItemListItemsResponseSchema = z.object({
  // Products newly added (already-member products don't count).
  addedCount: z.number().int().nonnegative(),
  unmatchedInputs: z.array(z.string()),
});

export const RemoveItemListItemsInputSchema = z.object({
  id: UUIDSchema,
  productKeys: z.array(z.string().min(1)).min(1).max(50000),
});

export const RemoveItemListItemsResponseSchema = z.object({
  removedCount: z.number().int().nonnegative(),
});

export const UpdateItemListInputSchema = z
  .object({
    id: UUIDSchema,
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
  })
  .refine(
    (input) => input.name !== undefined || input.description !== undefined,
    { message: "At least one field to update must be provided" },
  );

export const UpdateItemListResponseSchema = z.object({
  list: ItemListSchema,
});

export const DeleteItemListInputSchema = z.object({ id: UUIDSchema });

export const DeleteItemListResponseSchema = z.object({
  success: z.boolean(),
});

export const itemListsContracts = {
  list: oc.input(z.object({})).output(ListItemListsResponseSchema).route({
    method: "GET",
    summary: "List the org's shared item lists",
  }),
  get: oc
    .input(GetItemListInputSchema)
    .output(GetItemListResponseSchema)
    .route({
      method: "GET",
      summary: "Get an item list",
    }),
  create: oc
    .input(CreateItemListInputSchema)
    .output(CreateItemListResponseSchema)
    .route({
      method: "POST",
      summary:
        "Create an item list from product keys, item numbers, or catalog filters",
    }),
  addItems: oc
    .input(AddItemListItemsInputSchema)
    .output(AddItemListItemsResponseSchema)
    .route({
      method: "POST",
      summary: "Add products to an item list",
    }),
  removeItems: oc
    .input(RemoveItemListItemsInputSchema)
    .output(RemoveItemListItemsResponseSchema)
    .route({
      method: "POST",
      summary: "Remove products from an item list",
    }),
  update: oc
    .input(UpdateItemListInputSchema)
    .output(UpdateItemListResponseSchema)
    .route({
      method: "PATCH",
      summary: "Update an item list's name or description",
    }),
  delete: oc
    .input(DeleteItemListInputSchema)
    .output(DeleteItemListResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete an item list",
    }),
} as const;

export type ItemListDTO = z.infer<typeof ItemListSchema>;
export type ListItemListsResponse = z.infer<typeof ListItemListsResponseSchema>;
export type GetItemListResponse = z.infer<typeof GetItemListResponseSchema>;
export type ItemListSource = z.infer<typeof ItemListSourceSchema>;
export type ItemListItemsSource = z.infer<typeof ItemListItemsSourceSchema>;
export type CreateItemListInput = z.infer<typeof CreateItemListInputSchema>;
export type CreateItemListResponse = z.infer<
  typeof CreateItemListResponseSchema
>;
export type AddItemListItemsInput = z.infer<typeof AddItemListItemsInputSchema>;
export type AddItemListItemsResponse = z.infer<
  typeof AddItemListItemsResponseSchema
>;
export type RemoveItemListItemsInput = z.infer<
  typeof RemoveItemListItemsInputSchema
>;
export type RemoveItemListItemsResponse = z.infer<
  typeof RemoveItemListItemsResponseSchema
>;
export type UpdateItemListInput = z.infer<typeof UpdateItemListInputSchema>;
export type UpdateItemListResponse = z.infer<
  typeof UpdateItemListResponseSchema
>;
export type DeleteItemListResponse = z.infer<
  typeof DeleteItemListResponseSchema
>;
